/**
 * Parse and build Google Docs URLs for welcome-screen embeds.
 * Accepts full URLs (edit/view) and stores only document ID and optional tab ID.
 */

export interface WelcomeDocRef {
    docId: string | null;
    tabId: string | null;
}

/** Regex to extract doc ID from Google Docs URLs: /document/d/{id}/... */
const GOOGLE_DOC_PATH_REGEX = /\/document\/d\/([a-zA-Z0-9_-]+)/;

/** Extract tab id from query string: tab=t.xxx or tab=0 (sheet index) */
function parseTabFromSearch(search: string): string | null {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');
    if (tab == null || tab === '') return null;
    return tab;
}

/**
 * Parse a Google Docs URL (edit, view, or preview) and return doc ID and optional tab ID.
 * Flexible: handles docs.google.com/document/d/{id}/edit, /view, /preview with optional ?tab=...
 */
export function parseWelcomeDocUrl(url: string | null | undefined): WelcomeDocRef {
    if (url == null || typeof url !== 'string' || url.trim() === '') {
        return { docId: null, tabId: null };
    }
    const trimmed = url.trim();
    try {
        const u = new URL(trimmed);
        if (!u.hostname.includes('google.com') || !u.pathname.includes('/document/')) {
            return { docId: null, tabId: null };
        }
        const match = u.pathname.match(GOOGLE_DOC_PATH_REGEX);
        const docId = match ? match[1] : null;
        const tabId = parseTabFromSearch(u.search);
        return { docId, tabId: tabId ?? null };
    } catch {
        return { docId: null, tabId: null };
    }
}

/**
 * Build the preview URL for embedding in an iframe.
 * Use docId (required) and optional tabId.
 */
export function buildWelcomeDocPreviewUrl(welcomeRef: WelcomeDocRef): string | null {
    if (welcomeRef.docId == null || welcomeRef.docId === '') return null;
    const base = `https://docs.google.com/document/d/${welcomeRef.docId}/preview`;
    if (welcomeRef.tabId != null && welcomeRef.tabId !== '') {
        return `${base}?tab=${encodeURIComponent(welcomeRef.tabId)}`;
    }
    return base;
}

/**
 * Build the "open in Google Docs" view URL (for external link).
 */
export function buildWelcomeDocViewUrl(welcomeRef: WelcomeDocRef): string | null {
    if (welcomeRef.docId == null || welcomeRef.docId === '') return null;
    const base = `https://docs.google.com/document/d/${welcomeRef.docId}/edit`;
    if (welcomeRef.tabId != null && welcomeRef.tabId !== '') {
        return `${base}?tab=${encodeURIComponent(welcomeRef.tabId)}`;
    }
    return base;
}
